import { Alert } from 'antd'
import {
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import { FormSection } from 'features/libra/sections/sectionTypes'
import { resolveCloseoutAttribution } from 'features/libra/stepComponents/niiReport/closeoutAttribution'
import { NiiEligibilityTag } from 'features/libra/stepComponents/niiReport/EligibilityTag'
import { EditableStringList } from 'features/libra/stepComponents/niiReport/NiiFieldComponents'
import { NiiRecommendationsAiPanel } from 'features/libra/stepComponents/niiReport/NiiRecommendationsAiPanel'
import { type NiiReportConfig } from 'features/libra/stepComponents/niiReport/niiReportApi'
import { useNiiReportData } from 'features/libra/stepComponents/niiReport/NiiReportDataContext'
import { NII_REPORT_VALIDITY_NOTICE } from 'features/libra/stepComponents/niiReport/niiReportValidity'
import {
  DescriptionItem,
  OptionalDescriptions,
} from 'features/libra/stepComponents/niiReport/OptionalDescriptions'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { type EligibilityStatus } from 'features/libra/types/eligibility'
import React, { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { StyledCard } from './cardsStyles'

const WarningBanner = styled(Alert)`
  margin-bottom: 1rem;
`

interface NiiConclusionCardProps {
  eligibilityField: string
  justificationField: string
  recommendationsField: string
  recommendationsSection?: FormSection
  zoneFieldName: string
  preScreening?: NiiReportConfig['preScreening']
  readonly?: boolean
}

export const RawNiiConclusionCard: FC<NiiConclusionCardProps> = ({
  eligibilityField,
  justificationField,
  recommendationsField,
  recommendationsSection,
  zoneFieldName,
  preScreening,
  readonly,
}) => {
  const { localValues, setLocalFieldValue, isLoading } = useNiiReportData()
  // Earlier workflow steps own these saved results; closeout only reads them.
  const { fieldValues: ticketFieldValues } = useTicketAccess()

  const eligibility = unwrapFieldValue<EligibilityStatus>(
    ticketFieldValues[eligibilityField]
  )
  const { screenedOutAtPreScreening, justification: overrideJustification } =
    resolveCloseoutAttribution(
      ticketFieldValues,
      { eligibility: eligibilityField, justification: justificationField },
      preScreening
    )

  const recommendations = useMemo(
    () =>
      unwrapFieldValue<ReadonlyArray<string>>(
        localValues[recommendationsField]
      ) ?? [],
    [localValues, recommendationsField]
  )

  const handleSetRecommendations = useCallback(
    (value: ReadonlyArray<string>) => {
      setLocalFieldValue(recommendationsField, wrapFieldValue([...value]))
    },
    [setLocalFieldValue, recommendationsField]
  )

  const handleApplyAiRecommendations = useCallback(
    (value: ReadonlyArray<string>) => {
      setLocalFieldValue(recommendationsField, wrapFieldValue([...value]), {
        origin: { [recommendationsField]: 'llm' },
      })
    },
    [setLocalFieldValue, recommendationsField]
  )

  const handleAddRecommendation = useCallback(() => {
    handleSetRecommendations([...recommendations, ''])
  }, [handleSetRecommendations, recommendations])

  const recommendationsNode = useMemo(() => {
    if (!readonly) {
      return (
        <>
          {recommendationsSection && (
            <NiiRecommendationsAiPanel
              section={recommendationsSection}
              zoneFieldName={zoneFieldName}
              recommendations={recommendations}
              onApply={handleApplyAiRecommendations}
              onAddManual={handleAddRecommendation}
            />
          )}
          <EditableStringList
            key='recommendations'
            items={recommendations}
            setItems={handleSetRecommendations}
            notes
            showAddButton={!recommendationsSection}
          />
        </>
      )
    }

    if (recommendations.length === 0) {
      return '-'
    }

    return recommendations.map((item, index) => (
      <div key={`${item}-${index}`}>{item}</div>
    ))
  }, [
    readonly,
    recommendations,
    handleSetRecommendations,
    handleAddRecommendation,
    handleApplyAiRecommendations,
    recommendationsSection,
    zoneFieldName,
  ])

  const items = useMemo<ReadonlyArray<DescriptionItem>>(() => {
    const rows: DescriptionItem[] = [
      [
        'Final Eligibility',
        <NiiEligibilityTag key='eligibility' eligibility={eligibility} />,
      ],
    ]
    if (screenedOutAtPreScreening) {
      rows.push(['Pre-screening', 'Screened out at pre-screening'])
    } else if (overrideJustification) {
      rows.push(['Justification', overrideJustification])
    }
    rows.push(['Recommendations', recommendationsNode])
    return rows
  }, [
    eligibility,
    screenedOutAtPreScreening,
    overrideJustification,
    recommendationsNode,
  ])

  return (
    <StyledCard title='Conclusion'>
      <WarningBanner
        title={NII_REPORT_VALIDITY_NOTICE}
        type='warning'
        showIcon
      />
      <OptionalDescriptions isLoading={isLoading} items={items} />
    </StyledCard>
  )
}

export const NiiConclusionCard = React.memo(RawNiiConclusionCard)
